import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import tzLookup from 'tz-lookup';

const DASHBOARD_PERIOD_DAYS = { week: 10, month: 60, year: 455 };

const dateInputValue = (date) => date.toISOString().slice(0, 10);

const presetDateRange = (period) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - DASHBOARD_PERIOD_DAYS[period]);
  return { startDate: dateInputValue(start), endDate: dateInputValue(end) };
};

const signupBucket = (date, rangeDays) => {
  if (rangeDays <= 14) {
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return {
      label: date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' }),
      sortKey: day.getTime()
    };
  }
  if (rangeDays <= 90) {
    const week = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
    week.setHours(0, 0, 0, 0);
    return {
      label: `Wk of ${week.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
      sortKey: week.getTime()
    };
  }
  const month = new Date(date.getFullYear(), date.getMonth(), 1);
  return {
    label: date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
    sortKey: month.getTime()
  };
};

const DashboardPage = () => {
  const [dataView, setDataView] = useState('count'); // 'count', 'timezone', 'distance', 'distance-timezone'
  const [swims, setSwims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signupPreset, setSignupPreset] = useState('week');
  const [signupDateRange, setSignupDateRange] = useState(() => presetDateRange('week'));
  const [signups, setSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupsError, setSignupsError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const getDashboardData = httpsCallable(functions, 'getDashboardData');
      try {
        const result = await getDashboardData(signupDateRange);
        setSwims(result.data.swims || []);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [signupDateRange]);

  useEffect(() => {
    const fetchSignups = async () => {
      setSignupsLoading(true);
      setSignupsError(null);
      try {
        const getAuthSignupData = httpsCallable(functions, 'getAuthSignupData');
        const result = await getAuthSignupData(signupDateRange);
        setSignups(result.data.signups || []);
      } catch (err) {
        console.error('Failed to fetch Auth signup data:', err);
        setSignupsError('Unable to load Auth signup data.');
      } finally {
        setSignupsLoading(false);
      }
    };
    fetchSignups();
  }, [signupDateRange]);

  const rangeDays = useMemo(() => {
    const start = new Date(`${signupDateRange.startDate}T00:00:00`);
    const end = new Date(`${signupDateRange.endDate}T00:00:00`);
    return Math.max(1, Math.round((end - start) / 86400000) + 1);
  }, [signupDateRange]);

  const chartData = useMemo(() => {
    if (!swims.length) return [];

    const dataPoints = [];

    if (dataView === 'timezone') {
      swims.forEach(swim => {
        let tz = 'Unknown';
        if (swim.startCoordinate) {
          try {
            // startCoordinate is a Firestore GeoPoint, usually arrives as {_latitude, _longitude}
            tz = tzLookup(swim.startCoordinate._latitude, swim.startCoordinate._longitude);
          } catch (e) {}
        }
        const existing = dataPoints.find(d => d.label === tz);
        if (existing) {
          existing.value += 1;
        } else {
          dataPoints.push({ label: tz, value: 1, sortKey: tz });
        }
      });
    } else if (dataView === 'distance-timezone') {
      swims.forEach(swim => {
        let tz = 'Unknown';
        if (swim.startCoordinate) {
          try {
            // startCoordinate is a Firestore GeoPoint, usually arrives as {_latitude, _longitude}
            tz = tzLookup(swim.startCoordinate._latitude, swim.startCoordinate._longitude);
          } catch (e) {}
        }
        const distanceValue = swim.totalDistanceMeters || 0;
        const existing = dataPoints.find(d => d.label === tz);
        if (existing) {
          existing.value += distanceValue;
        } else {
          dataPoints.push({ label: tz, value: distanceValue, sortKey: tz });
        }
      });
    } else {
      swims.forEach(swim => {
        const date = new Date(swim.startDate);
        const { label, sortKey } = signupBucket(date, rangeDays);

        const value = dataView === 'distance' ? (swim.totalDistanceMeters || 0) : 1;
        const existing = dataPoints.find(d => d.sortKey === sortKey);
        if (existing) {
          existing.value += value;
        } else {
          dataPoints.push({ label, value, sortKey });
        }
      });
    }

    // Sort dataPoints chronologically, by count (timezone), or alphabetically
    dataPoints.sort((a, b) => {
      if (dataView === 'timezone' || dataView === 'distance-timezone') {
        // For timezone views, sort by count/distance descending
        return b.value - a.value;
      } else if (typeof a.sortKey === 'number' && typeof b.sortKey === 'number') {
        // For time-based periods, sort chronologically
        return a.sortKey - b.sortKey;
      } else {
        // Fallback to alphabetical
        return a.sortKey.localeCompare(b.sortKey);
      }
    });

    return dataPoints.map(({ label, value }) => ({ label, value }));
  }, [swims, dataView, rangeDays]);

  const signupChartData = useMemo(() => {
    const buckets = new Map();

    signups.forEach((signup) => {
      const { label, sortKey } = signupBucket(new Date(signup), rangeDays);
      const bucket = buckets.get(sortKey) || { label, value: 0, sortKey };
      bucket.value += 1;
      buckets.set(sortKey, bucket);
    });

    return [...buckets.values()]
      .sort((first, second) => first.sortKey - second.sortKey)
      .map(({ label, value }) => ({ label, value }));
  }, [signups, rangeDays]);

  const applySignupPreset = (nextPreset) => {
    setSignupPreset(nextPreset);
    if (nextPreset !== 'custom') setSignupDateRange(presetDateRange(nextPreset));
  };

  const updateSignupDate = (field, value) => {
    setSignupPreset('custom');
    setSignupDateRange((current) => ({ ...current, [field]: value }));
  };

  const formatValue = (val) => {
    if (dataView === 'distance' || dataView === 'distance-timezone') {
      return val >= 1000 ? `${(val / 1000).toFixed(1)}km` : `${Math.round(val)}m`;
    }
    return val;
  };

  const getDisplayLabel = (label) => {
    if (dataView === 'timezone' || dataView === 'distance-timezone') {
      // Remove country code and slash (e.g., "America/New_York" -> "New_York")
      return label.includes('/') ? label.split('/')[1] : label;
    }
    return label;
  };

  const BarChart = ({ data, formatChartValue = formatValue, formatChartLabel = getDisplayLabel }) => {
    if (data.length === 0) return <div style={{padding: '40px', textAlign: 'center'}}>No data available for this selection.</div>;
    const max = Math.max(...data.map(d => d.value), 1);
    
    return (
      <div className="chart-wrapper" style={{ overflowX: 'auto', marginTop: '20px' }}>
        <div className="chart-container" style={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: '12px', 
          height: '350px', 
          padding: '20px 40px', 
          borderBottom: '1px solid #eee'
        }}>
          {/* Bars row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', flex: 1 }}>
            {data.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '11px', marginBottom: '5px', fontWeight: 'bold', writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{formatChartValue(d.value)}</span>
                <div style={{ 
                  width: '100%', 
                  backgroundColor: '#007bff', 
                  height: `${(d.value / max) * 100}%`, 
                  minHeight: '2px',
                  borderRadius: '4px 4px 0 0',
                  transition: 'height 0.3s ease'
                }} title={`${d.label}: ${d.value}`}></div>
              </div>
            ))}
          </div>
          {/* Labels row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2px' }}>
            {data.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <span style={{ fontSize: '10px', writingMode: 'vertical-rl', textOrientation: 'mixed', whiteSpace: 'nowrap', textAlign: 'center' }}>{formatChartLabel(d.label)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-page" style={{ padding: '20px' }}>
      <section className="dashboard-signups">
        <div className="dashboard-date-picker">
          <div className="form-group">
            <label htmlFor="dashboard-range-preset">Date range:</label>
            <select id="dashboard-range-preset" value={signupPreset} onChange={(event) => applySignupPreset(event.target.value)}>
              <option value="week">Last Week</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="dashboard-start-date">Start:</label>
            <input
              id="dashboard-start-date"
              type="date"
              value={signupDateRange.startDate}
              onChange={(event) => updateSignupDate('startDate', event.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="dashboard-end-date">End:</label>
            <input
              id="dashboard-end-date"
              type="date"
              value={signupDateRange.endDate}
              onChange={(event) => updateSignupDate('endDate', event.target.value)}
            />
          </div>
        </div>
        <h2>New Auth Signups</h2>
        {signupsError && <p className="error">{signupsError}</p>}
        {signupsLoading ? <p>Loading Auth signups...</p> : (
          <BarChart data={signupChartData} formatChartValue={(value) => value} formatChartLabel={(label) => label} />
        )}
      </section>

      <section className="dashboard-swims">
        <h2>Tracked Swims</h2>
        <div className="dashboard-swim-controls">
          <div className="form-group">
            <label style={{ fontWeight: 'bold', marginRight: '8px' }}>Data View:</label>
            <select value={dataView} onChange={e => setDataView(e.target.value)} style={{ padding: '6px', borderRadius: '4px' }}>
              <option value="count">Swims per Period</option>
              <option value="timezone">Swims by Timezone</option>
              <option value="distance">Distance per Period</option>
              <option value="distance-timezone">Distance by Timezone</option>
            </select>
          </div>
        </div>
        {loading ? <p>Loading dashboard statistics...</p> : <BarChart data={chartData} />}
      </section>
    </div>
  );
};

export default DashboardPage;